import { useMemo } from 'react';
import { Result } from 'libs/end-to-end/src/result';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { authenticatedApiCall } from 'apps/sigma2-ui/src/lib/api';
import { deserializeError } from 'libs/end-to-end/src/api-error';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { StateAuthenticatedApiCallOptions } from 'apps/sigma2-ui/src/state/user-types';
import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { useAlive } from 'apps/sigma2-ui/src/lib/hooks/useAlive';

export const useUserAuthenticatedApiCall = (
  onUrlCallback?: (originalUrl: string) => string,
): UserAuthenticatedApiCall => {
  const isAlive = useAlive();

  return useMemo(() => {
    return async <TResponse>(
      options: StateAuthenticatedApiCallOptions<TResponse>,
    ): Promise<Result<TResponse, BaseError> | null> => {
      if (!isAlive()) {
        return null;
      }

      const originalUrl = options.url;
      const modifiedUrl = !onUrlCallback
        ? originalUrl
        : onUrlCallback(originalUrl);

      let result;
      try {
        result = await authenticatedApiCall({
          ...options,
          url: modifiedUrl,
          deserializeErrorFn: deserializeError,
        });
      } catch (exc) {
        if (exc instanceof Error) {
          uiAppLogger.error(
            `Caught ${exc.name} during authenticatedApiCall:`,
            exc.message,
          );
        }
        return null;
      }

      if (!isAlive()) {
        return null;
      }

      return result;
    };
  }, [isAlive, onUrlCallback]);
};
